import React, { useMemo } from 'react';
import {
  Search,
  Wifi,
  Bell,
  Bookmark,
  Filter,
  ExternalLink,
  Eye,
  Newspaper,
} from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';
import { colors } from '../../config/colors';
import { Card } from '../UI/card';
import { Button } from '../UI/button';
import { Badge } from '../UI/badge';
import { Input } from '../UI/input';
import LoadingSpinner from '../UI/LoadingSpinner';
import { formatRelativeTime } from '../../utils/companyDisplayUtils';

/** Normalize article sentiment for filtering/counts; handles sentiment, sentiment_label, sentimentLabel, etc. */
export function getArticleSentiment(article) {
  const raw = (article?.sentiment ?? article?.sentiment_label ?? article?.sentimentLabel ?? 'neutral');
  const s = String(raw).toLowerCase().trim();
  if (s === 'positive' || s === 'negative') return s;
  return 'neutral';
}

export function getSeverity(article) {
  const sentiment = getArticleSentiment(article);
  const relevance = Number(article?.relevanceScore ?? article?.relevance_score) || 0;
  if (sentiment === 'negative' && relevance >= 0.7) return 'critical';
  if (sentiment === 'negative' || relevance >= 0.6) return 'high';
  return 'medium';
}

function getSeverityStyle(severity) {
  if (severity === 'critical') return { border: colors.danger[500], label: colors.danger[600], bg: colors.danger[50] };
  if (severity === 'high') return { border: colors.warning[500], label: colors.warning[600], bg: colors.warning[50] };
  return { border: colors.gray[400], label: colors.text.secondary, bg: colors.gray[50] };
}

function formatImpactedName(c) {
  if (typeof c === 'string') return c;
  return c?.name ?? c?.ticker ?? '';
}

function NewsCard({ article, t, onSave, isSaved = false }) {
  const severity = article.severity || getSeverity(article);
  const severityStyle = getSeverityStyle(severity);
  const rt = formatRelativeTime(article.publishedAt);
  const timeText = rt.relativeKey ? t(rt.relativeKey, rt.relativeParams) : rt.date;
  const tags = article.riskCategories?.length ? article.riskCategories : (article.keywords || []).slice(0, 4);
  const impacted = [];
  if (article.ticker || article.companyName) {
    impacted.push(article.companyName || article.ticker || '');
  }
  if (article.impactedCompanies?.length) {
    impacted.push(...article.impactedCompanies.slice(0, 5).map(formatImpactedName));
  }
  const moreCount = (article.impactedCompanies?.length || 0) - 5;
  const severityLabel = severity === 'critical' ? t('severityCritical') : severity === 'high' ? t('severityHigh') : null;

  return (
    <article
      className="rounded-xl border overflow-hidden transition-shadow hover:shadow-md flex"
      style={{ backgroundColor: colors.background.card, borderColor: colors.border.light }}
    >
      <div
        className="w-1 flex-shrink-0 self-stretch"
        style={{ backgroundColor: severityStyle.border }}
      />
      <div className="flex-1 min-w-0 p-5">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex flex-wrap items-center gap-2 text-xs" style={{ color: colors.text.tertiary }}>
            {severityLabel && (
              <span className="font-semibold" style={{ color: severityStyle.label }}>{severityLabel}</span>
            )}
            <span>{article.source || '—'}</span>
            <span>·</span>
            <span>{timeText}</span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button variant="ghost" size="icon-sm" aria-label={isSaved ? t('unsaveArticle') : t('saveArticle')} onClick={() => onSave?.(article)}>
              <Bookmark className="h-4 w-4" style={{ color: isSaved ? (colors.bookmark?.saved ?? colors.warning[500]) : colors.text.tertiary }} fill={isSaved ? (colors.bookmark?.saved ?? colors.warning[500]) : 'transparent'} />
            </Button>
            {article.url && (
              <a
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 transition-colors"
                style={{ color: colors.text.tertiary }}
                aria-label={t('readArticle')}
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>
        </div>
        <h3 className="text-lg font-semibold leading-snug mb-2" style={{ color: colors.text.primary }}>
          {article.title}
        </h3>
        {article.description && (
          <p className="text-sm leading-relaxed mb-3 line-clamp-3" style={{ color: colors.text.secondary }}>
            {article.description}
          </p>
        )}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {tags.map((tag, i) => (
              <span
                key={i}
                className="text-xs px-2 py-0.5 rounded-full"
                style={{ backgroundColor: colors.gray[100], color: colors.text.secondary }}
              >
                {typeof tag === 'string' ? tag : (tag?.label ?? tag?.name ?? tag)}
              </span>
            ))}
          </div>
        )}
        {impacted.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium" style={{ color: colors.text.secondary }}>
              {t('impactedCompanies')}
            </span>
            {impacted.slice(0, 3).map((c, i) => (
              <Badge key={i} variant="secondary" className="text-xs">{c}</Badge>
            ))}
            {moreCount > 0 && (
              <span className="text-xs" style={{ color: colors.text.tertiary }}>{t('moreCompanies', { count: moreCount })}</span>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

export default function RiskIntelligenceFeed({
  articles = [],
  loading = false,
  compact = false,
  showHeader = true,
  search = '',
  onSearchChange,
  selectedTopic = 'all',
  onTopicChange,
  selectedRegion = 'all',
  onRegionChange,
  selectedSeverity = 'all',
  onSeverityChange,
  onFilterClick,
  onClearFilters,
  quickFilterCounts = { allNews: 0, positiveCount: 0, negativeCount: 0, savedArticles: 0, watchlistNews: 0 },
  activeQuickFilter = 'all',
  onQuickFilterChange,
  trendingTopics = [],
  pagination = null,
  onPageChange,
  onSaveArticle,
  onArticleMenu,
  hasActiveFilters = false,
  companies = [],
  selectedTicker = '',
  onTickerChange,
  selectedIndustry = '',
  onIndustryChange,
  dateFrom = '',
  dateTo = '',
  onDateFromChange,
  onDateToChange,
  showSentimentFilter = false,
  selectedSentiment = 'all',
  onSentimentChange,
  inlineTitle = null,
  filterOptions = null,
  savedArticleIds = null,
  showWatchlistQuickFilter = true,
  showAlertSettings = true,
  showSidebar = true,
  applyFiltersImmediately = false,
  compactFiltersOnly = false,
  showSentimentQuickFilters = true,
  showSavedQuickFilter = true,
}) {
  const { t } = useLocale();
  const industries = useMemo(
    () => [...new Set((companies || []).map((c) => c.industry).filter(Boolean))].sort(),
    [companies]
  );

  const searchIconLeft = showSidebar ? 'left-2.5' : 'left-3';
  const searchInputPl = showSidebar ? 'pl-9' : 'pl-10';
  const searchBox = (
    <div className={showSidebar ? 'relative shrink-0 min-w-[160px] max-w-[260px] w-full sm:w-auto' : 'relative flex-1'}>
      <Search
        className={`absolute ${searchIconLeft} top-1/2 -translate-y-1/2 h-4 w-4`}
        style={{ color: colors.text.tertiary }}
      />
      <Input
        type="text"
        placeholder={t('searchNewsPlaceholder')}
        value={search}
        onChange={(e) => onSearchChange?.(e.target.value)}
        className={`${searchInputPl} h-9 text-sm w-full`}
        style={{ borderColor: showSidebar ? colors.border.medium : colors.border.light }}
      />
    </div>
  );

  const selectClassName = showSidebar
    ? 'h-9 px-3 rounded-lg border text-sm min-w-0'
    : 'rounded-lg border text-sm px-3 py-2 min-w-[140px] focus:outline-none focus:ring-2 focus:ring-primary-500';
  const selectStyle = showSidebar
    ? { borderColor: colors.border.medium, color: colors.text.primary }
    : { borderColor: colors.border.light, color: colors.text.primary, backgroundColor: colors.background.secondary };

  const quickFilterChips = (
    <div className={`flex flex-wrap items-center gap-2 ${compactFiltersOnly ? '' : 'ml-auto'}`}>
      <button
        type="button"
        onClick={() => onQuickFilterChange?.('all')}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
        style={{
          backgroundColor: activeQuickFilter === 'all' ? colors.primary[50] : 'transparent',
          color: activeQuickFilter === 'all' ? colors.primary[700] : colors.text.secondary,
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: activeQuickFilter === 'all' ? colors.primary[200] : colors.border.medium,
        }}
      >
        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: colors.primary[500] }} />
        {t('allNewsCount', { count: quickFilterCounts.allNews ?? articles.length })}
      </button>
      {showSentimentQuickFilters && (
        <>
          <button
            type="button"
            onClick={() => onQuickFilterChange?.('positive')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
            style={{
              backgroundColor: activeQuickFilter === 'positive' ? colors.success[50] : 'transparent',
              color: activeQuickFilter === 'positive' ? colors.success[700] : colors.text.secondary,
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: activeQuickFilter === 'positive' ? colors.success[200] : colors.border.medium,
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: colors.success[500] }} />
            {t('positiveCount', { count: quickFilterCounts.positiveCount ?? articles.filter((a) => getArticleSentiment(a) === 'positive').length })}
          </button>
          <button
            type="button"
            onClick={() => onQuickFilterChange?.('negative')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
            style={{
              backgroundColor: activeQuickFilter === 'negative' ? colors.danger[50] : 'transparent',
              color: activeQuickFilter === 'negative' ? colors.danger[700] : colors.text.secondary,
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: activeQuickFilter === 'negative' ? colors.danger[200] : colors.border.medium,
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: colors.danger[500] }} />
            {t('negativeCount', { count: quickFilterCounts.negativeCount ?? articles.filter((a) => getArticleSentiment(a) === 'negative').length })}
          </button>
        </>
      )}
      {showSavedQuickFilter && (
        <button
          type="button"
          onClick={() => onQuickFilterChange?.('saved')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
          style={{
            backgroundColor: activeQuickFilter === 'saved' ? (colors.bookmark?.savedMuted ?? colors.warning[50]) : 'transparent',
            color: activeQuickFilter === 'saved' ? (colors.bookmark?.savedMuted ?? colors.warning[700]) : colors.text.secondary,
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: activeQuickFilter === 'saved' ? (colors.bookmark?.saved ?? colors.warning[200]) : colors.border.medium,
          }}
        >
          <Bookmark className="h-4 w-4 shrink-0" style={{ color: activeQuickFilter === 'saved' ? (colors.bookmark?.saved ?? colors.warning[500]) : colors.text.tertiary }} fill={activeQuickFilter === 'saved' ? (colors.bookmark?.saved ?? colors.warning[500]) : 'transparent'} />
          {t('savedArticlesCount', { count: quickFilterCounts.savedArticles ?? 0 })}
        </button>
      )}
      {showWatchlistQuickFilter && (
        <button
          type="button"
          onClick={() => onQuickFilterChange?.('watchlist')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
          style={{
            backgroundColor: activeQuickFilter === 'watchlist' ? colors.primary[50] : 'transparent',
            color: activeQuickFilter === 'watchlist' ? colors.primary[700] : colors.text.secondary,
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: activeQuickFilter === 'watchlist' ? colors.primary[200] : colors.border.medium,
          }}
        >
          <Eye className="h-4 w-4 shrink-0" style={{ color: colors.text.tertiary }} />
          {t('watchlistNewsCount', { count: quickFilterCounts.watchlistNews ?? 0 })}
        </button>
      )}
    </div>
  );

  const filtersGroup = (
    <div className={inlineTitle ? 'flex flex-wrap items-center justify-between gap-2 w-full' : 'flex flex-wrap items-center gap-2'}>
      {compactFiltersOnly ? (
        <>
          {hasActiveFilters && (
            <button
              type="button"
              className="text-sm font-medium hover:underline"
              style={{ color: colors.primary[600] }}
              onClick={onClearFilters}
            >
              {t('clearFilters')}
            </button>
          )}
          {quickFilterChips}
        </>
      ) : (
        <>
      {companies?.length > 0 && (
        <select
          value={selectedTicker}
          onChange={(e) => onTickerChange?.(e.target.value)}
          className={selectClassName}
          style={selectStyle}
          aria-label="Company"
        >
          <option value="">{t('allCompanies')}</option>
          {companies
            .filter((c) => (c.ticker || c.company_id || '').toString().trim())
            .map((c) => {
              const ticker = (c.ticker || c.company_id || '').toString().trim();
              return (
                <option key={ticker} value={ticker}>
                  {ticker} — {c.name || ticker}
                </option>
              );
            })}
          </select>
      )}
      {industries.length > 0 && (
        <select
          value={selectedIndustry}
          onChange={(e) => onIndustryChange?.(e.target.value)}
          className={selectClassName}
          style={selectStyle}
          aria-label="Industry"
        >
          <option value="">{t('allIndustries')}</option>
          {industries.map((ind) => (
            <option key={ind} value={ind}>{ind}</option>
          ))}
        </select>
      )}
      <select
        value={selectedTopic}
        onChange={(e) => onTopicChange?.(e.target.value)}
        className={selectClassName}
        style={selectStyle}
        aria-label={t('allTopics')}
      >
        <option value="all">{t('allTopics')}</option>
        {filterOptions?.topics?.map((opt) => (
          <option key={opt.id} value={opt.id}>
            {opt.label ?? opt.id}{opt.count != null ? ` (${opt.count})` : ''}
          </option>
        ))}
      </select>
      <select
        value={selectedRegion}
        onChange={(e) => onRegionChange?.(e.target.value)}
        className={selectClassName}
        style={selectStyle}
        aria-label={t('allRegions')}
      >
        <option value="all">{t('allRegions')}</option>
        {filterOptions?.regions?.map((opt) => (
          <option key={opt.id} value={opt.id}>
            {opt.label ?? opt.id}{opt.count != null ? ` (${opt.count})` : ''}
          </option>
        ))}
      </select>
      <select
        value={selectedSeverity}
        onChange={(e) => onSeverityChange?.(e.target.value)}
        className={selectClassName}
        style={selectStyle}
        aria-label={t('allSeverity')}
      >
        <option value="all">{t('allSeverity')}</option>
        {filterOptions?.severityLevels?.map((opt) => (
          <option key={opt.id} value={opt.id}>
            {opt.label ?? opt.id}{opt.count != null ? ` (${opt.count})` : ''}
          </option>
        ))}
      </select>
      {showSentimentFilter && (
        <>
          <Button
            variant={selectedSentiment === 'all' ? 'default' : 'outline'}
            size="sm"
            className="h-9 px-3"
            onClick={() => onSentimentChange?.('all')}
          >
            {t('filterAll')}
          </Button>
          <Button
            variant={selectedSentiment === 'positive' ? 'default' : 'outline'}
            size="sm"
            className="h-9 px-3"
            onClick={() => onSentimentChange?.('positive')}
          >
            {t('sentimentPositive')}
          </Button>
          <Button
            variant={selectedSentiment === 'negative' ? 'default' : 'outline'}
            size="sm"
            className="h-9 px-3"
            onClick={() => onSentimentChange?.('negative')}
          >
            {t('sentimentNegative')}
          </Button>
          <Button
            variant={selectedSentiment === 'neutral' ? 'default' : 'outline'}
            size="sm"
            className="h-9 px-3"
            onClick={() => onSentimentChange?.('neutral')}
          >
            {t('sentimentNeutral')}
          </Button>
        </>
      )}
      {showWatchlistQuickFilter && (
        <select
          value={activeQuickFilter === 'watchlist' ? 'watchlist' : 'all'}
          onChange={(e) => onQuickFilterChange?.(e.target.value === 'watchlist' ? 'watchlist' : 'all')}
          className={selectClassName}
          style={selectStyle}
          aria-label={t('newsViewScope')}
        >
          <option value="all">{t('viewAll')}</option>
          <option value="watchlist">{t('myCompaniesTitle')}</option>
        </select>
      )}
      {!applyFiltersImmediately && (
        <Button size="sm" className="h-9 px-3" onClick={onFilterClick}>
          <Filter className="h-4 w-4 mr-1.5" />
          {t('filter')}
        </Button>
      )}
      {hasActiveFilters && (
        <button
          type="button"
          className="text-sm font-medium hover:underline"
          style={{ color: colors.primary[600] }}
          onClick={onClearFilters}
        >
          {showSidebar ? t('clearFilters') : t('clearAll')}
        </button>
      )}
      {quickFilterChips}
        </>
      )}
    </div>
  );

  const filteredArticles = useMemo(() => {
    let list = articles;
    if (showSavedQuickFilter && activeQuickFilter === 'saved' && savedArticleIds != null) {
      list = list.filter((a) => {
        const id = a?.id ?? a?.articleId ?? a?.article_id;
        return id != null && savedArticleIds.has?.(String(id).trim());
      });
    } else if (showSentimentQuickFilters && activeQuickFilter === 'positive') {
      list = list.filter((a) => getArticleSentiment(a) === 'positive');
    } else if (showSentimentQuickFilters && activeQuickFilter === 'negative') {
      list = list.filter((a) => getArticleSentiment(a) === 'negative');
    } else if (activeQuickFilter === 'all' && showSentimentFilter && selectedSentiment !== 'all') {
      list = list.filter((a) => getArticleSentiment(a) === selectedSentiment.toLowerCase().trim());
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (a) =>
          (a.title || '').toLowerCase().includes(q) ||
          (a.description || '').toLowerCase().includes(q) ||
          (a.source || '').toLowerCase().includes(q) ||
          (a.ticker || '').toLowerCase().includes(q) ||
          (a.companyName || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [articles, activeQuickFilter, savedArticleIds, search, showSentimentFilter, selectedSentiment, showSentimentQuickFilters, showSavedQuickFilter]);

  return (
    <div className="space-y-6">
      {showHeader && (
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1
              className={compact ? 'text-xl font-bold' : 'text-2xl font-bold'}
              style={{ color: colors.text.primary }}
            >
              {t('riskIntelligenceFeedTitle')}
            </h1>
            <p className="text-sm mt-0.5" style={{ color: colors.text.secondary }}>
              {t('riskIntelligenceFeedSubtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm">
              <Wifi className="h-4 w-4 mr-2" />
              {t('subscribe')}
            </Button>
            <Button size="sm">
              <Bell className="h-4 w-4 mr-2" />
              {t('alerts')}
            </Button>
            <Button variant="ghost" size="icon" aria-label={t('alerts')}>
              <Bell className="h-4 w-4" style={{ color: colors.text.secondary }} />
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
        <div className="flex-1 min-w-0 space-y-4">
          {inlineTitle ? (
            <div className="flex flex-col gap-3 w-full">
              <div className="flex items-center justify-between w-full gap-3">
                <h2 className="text-lg font-semibold whitespace-nowrap" style={{ color: colors.text.primary }}>{inlineTitle}</h2>
                {searchBox}
              </div>
              <div className="w-full">
                {filtersGroup}
              </div>
            </div>
          ) : showSidebar ? (
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 min-w-[140px]">{searchBox}</div>
              {filtersGroup}
            </div>
          ) : (
            <Card className="p-6" style={{ backgroundColor: colors.background.secondary, borderColor: colors.border.light }}>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                  {searchBox}
                  <Button variant="default" size="default" className="shrink-0" onClick={onFilterClick}>
                    <Search className="h-4 w-4" style={{ color: colors.text.inverse }} />
                    {t('searchButton')}
                  </Button>
                </div>
                <div className="flex flex-wrap items-center gap-3 pt-2 border-t" style={{ borderColor: colors.border.light }}>
                  <span className="text-sm font-medium" style={{ color: colors.text.secondary }}>
                    {t('filtersLabel')}
                  </span>
                  {filtersGroup}
                </div>
              </div>
            </Card>
          )}

          {loading ? (
            <div className="flex justify-center items-center py-16">
              <LoadingSpinner />
            </div>
          ) : filteredArticles.length === 0 ? (
            <Card className="p-12 text-center" style={{ borderColor: colors.border.light }}>
              <Newspaper className="h-14 w-14 mx-auto mb-4" style={{ color: colors.text.tertiary }} />
              <h3 className="text-xl font-semibold mb-2" style={{ color: colors.text.primary }}>
                {t('noArticlesFound')}
              </h3>
              <p className="text-sm" style={{ color: colors.text.secondary }}>
                {hasActiveFilters || search.trim() ? t('tryChangingFilters') : t('noNewsAvailable')}
              </p>
            </Card>
          ) : (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
              {filteredArticles.map((article) => (
                <NewsCard
                  key={article.id || article.articleId || article.url || article.title}
                  article={article}
                  t={t}
                  onSave={onSaveArticle}
                  isSaved={savedArticleIds != null && (() => {
                    const id = article?.id ?? article?.articleId ?? article?.article_id;
                    return id != null && savedArticleIds.has?.(String(id).trim());
                  })()}
                />
              ))}
            </div>
          )}

          {pagination && pagination.totalPages > 1 && onPageChange && (
            <div
              className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t"
              style={{ borderColor: colors.border.light }}
            >
              <p className="text-sm" style={{ color: colors.text.secondary }}>
                {t('paginationPageOf', { page: pagination.page, total: pagination.totalPages })}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(pagination.page - 1)}
                  disabled={!pagination.hasPrevious}
                >
                  {t('paginationPrevious')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(pagination.page + 1)}
                  disabled={!pagination.hasNext}
                >
                  {t('paginationNext')}
                </Button>
              </div>
            </div>
          )}
        </div>

        {showSidebar && (
          <aside className="w-full lg:w-80 flex-shrink-0 space-y-4">
            <Card className="p-4" style={{ borderColor: colors.border.light }}>
              <h3 className="text-sm font-semibold mb-3" style={{ color: colors.text.primary }}>
                {t('quickFilters')}
              </h3>
              <ul className="space-y-2">
                <li>
                  <button
                    type="button"
                    onClick={() => onQuickFilterChange?.('all')}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors"
                    style={{
                      backgroundColor: activeQuickFilter === 'all' ? colors.primary[50] : 'transparent',
                      color: activeQuickFilter === 'all' ? colors.primary[700] : colors.text.primary,
                    }}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.primary[500] }} />
                    {t('allNewsCount', { count: quickFilterCounts.allNews ?? articles.length })}
                  </button>
                </li>
                {showSentimentQuickFilters && (
                  <>
                    <li>
                      <button
                        type="button"
                        onClick={() => onQuickFilterChange?.('positive')}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors"
                        style={{
                          backgroundColor: activeQuickFilter === 'positive' ? colors.success[50] : 'transparent',
                          color: activeQuickFilter === 'positive' ? colors.success[700] : colors.text.primary,
                        }}
                      >
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.success[500] }} />
                        {t('positiveCount', { count: quickFilterCounts.positiveCount ?? articles.filter((a) => getArticleSentiment(a) === 'positive').length })}
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        onClick={() => onQuickFilterChange?.('negative')}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors"
                        style={{
                          backgroundColor: activeQuickFilter === 'negative' ? colors.danger[50] : 'transparent',
                          color: activeQuickFilter === 'negative' ? colors.danger[700] : colors.text.primary,
                        }}
                      >
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.danger[500] }} />
                        {t('negativeCount', { count: quickFilterCounts.negativeCount ?? articles.filter((a) => getArticleSentiment(a) === 'negative').length })}
                      </button>
                    </li>
                  </>
                )}
                {showSavedQuickFilter && (
                  <li>
                    <button
                      type="button"
                      onClick={() => onQuickFilterChange?.('saved')}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors"
                      style={{
                        backgroundColor: activeQuickFilter === 'saved' ? colors.warning[50] : 'transparent',
                        color: activeQuickFilter === 'saved' ? (colors.bookmark?.savedMuted ?? colors.warning[700]) : colors.text.primary,
                      }}
                    >
                      <Bookmark className="h-4 w-4" style={{ color: activeQuickFilter === 'saved' ? (colors.bookmark?.saved ?? colors.warning[500]) : colors.text.tertiary }} fill={activeQuickFilter === 'saved' ? (colors.bookmark?.saved ?? colors.warning[500]) : 'transparent'} />
                      {t('savedArticlesCount', { count: quickFilterCounts.savedArticles ?? 0 })}
                    </button>
                  </li>
                )}
                {showWatchlistQuickFilter && (
                  <li>
                    <button
                      type="button"
                      onClick={() => onQuickFilterChange?.('watchlist')}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors"
                      style={{
                        backgroundColor: activeQuickFilter === 'watchlist' ? colors.primary[50] : 'transparent',
                        color: colors.text.primary,
                      }}
                    >
                      <Eye className="h-4 w-4" style={{ color: colors.text.tertiary }} />
                      {t('watchlistNewsCount', { count: quickFilterCounts.watchlistNews ?? 0 })}
                    </button>
                  </li>
                )}
              </ul>
            </Card>

            {trendingTopics.length > 0 && (
              <Card className="p-4" style={{ borderColor: colors.border.light }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: colors.text.primary }}>
                  {t('trendingTopics')}
                </h3>
                <ul className="space-y-2">
                  {trendingTopics.map((topic) => (
                    <li
                      key={topic.id || topic.label}
                      className="flex items-center justify-between px-3 py-2 rounded-lg text-sm"
                      style={{ backgroundColor: colors.gray[50] }}
                    >
                      <span style={{ color: colors.text.primary }}>{topic.label}</span>
                      <span
                        className="text-xs font-medium px-2 py-0.5 rounded"
                        style={{
                          backgroundColor: (topic.changePercent || 0) >= 50 ? colors.warning[100] : colors.success[100],
                          color: (topic.changePercent || 0) >= 50 ? colors.warning[700] : colors.success[700],
                        }}
                      >
                        +{topic.changePercent ?? 0}%
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {showAlertSettings && (
              <Card className="p-4" style={{ borderColor: colors.border.light }}>
                <h3 className="text-sm font-semibold" style={{ color: colors.text.primary }}>
                  {t('alertSettings')}
                </h3>
                <p className="text-xs mt-1" style={{ color: colors.text.tertiary }}>
                  {t('alertSettingsDescription')}
                </p>
              </Card>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
